import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { catchError, concatMap, from, map, Observable, of, toArray } from 'rxjs';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTableModule } from '@angular/material/table';

import { AgentTeamService } from '../../services/agent-team.service';
import { TenantUserService } from '../../services/tenant-user.service';
import {
  AgentTeamCheckKonto,
  AgentTeamCheckResponse,
  AgentTeamErrorBody,
  AgentTeamImportResponse,
  AgentTeamInvalidFehler,
  AgentTeamMitgliedschaft,
  AgentTeamNichtMehrInDatei,
  AgentTeamStand,
} from '../../models/agent-team.model';
import { AGENT_ENV_FILE_NAME, buildAgentEnvFile } from '../../shared/agent-env-file';
import { saveBlob } from '../../shared/file-download';
import { AvatarComponent } from '../../components/avatar/avatar.component';
import {
  StatusPillComponent,
  StatusKind,
  customerStatusKind,
} from '../../components/status-pill/status-pill.component';
import { KiKontoPillComponent } from '../../components/ki-konto-pill/ki-konto-pill.component';
import { FileDropzoneComponent } from '../../components/file-dropzone/file-dropzone.component';
import { ConfirmDialogComponent } from '../../components/confirm-dialog/confirm-dialog.component';
import { StatGroupComponent, StatItem } from '../../components/stat-group/stat-group.component';

/** Same limit as the server (1 048 576 bytes); `File.size` counts bytes, not characters. */
const MAX_FILE_BYTES = 1_048_576;

const STAND_KIND: Record<AgentTeamStand, StatusKind> = {
  NEU: 'laufend',
  VORHANDEN: 'aktiv',
  FEHLER: 'fehler',
};

const KONTO_COLUMNS = ['name', 'stand', 'mitgliedschaften', 'token', 'fehler'] as const;
const NICHT_MEHR_COLUMNS = ['select', 'name', 'status'] as const;

const KNOWN_ROLES: ReadonlySet<string> = new Set(['PROJECT_READER', 'PROJECT_WRITER']);

type FileError = 'tooLarge' | 'invalidJson';

interface DeactivationOutcome {
  userId: string;
  ok: boolean;
}

/**
 * Upload of an AI team file (NEX-1986): preview, "Anlegen" with `zugaenge.env` download and deactivation of
 * accounts the file no longer names. Token values live only in this page and leave the browser only as download.
 */
@Component({
  selector: 'app-agent-team-upload-page',
  imports: [
    TranslatePipe,
    MatIconModule,
    MatCheckboxModule,
    MatTableModule,
    AvatarComponent,
    StatusPillComponent,
    KiKontoPillComponent,
    FileDropzoneComponent,
    ConfirmDialogComponent,
    StatGroupComponent,
  ],
  templateUrl: './agent-team-upload-page.component.html',
  styleUrl: './agent-team-upload-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgentTeamUploadPageComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly agentTeamService = inject(AgentTeamService);
  private readonly tenantUserService = inject(TenantUserService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translate = inject(TranslateService);

  private readonly customerId = this.route.snapshot.paramMap.get('customerId') ?? '';
  private teamFile: unknown = null;

  readonly fileError = signal<FileError | null>(null);
  readonly checking = signal(false);
  readonly checkResult = signal<AgentTeamCheckResponse | null>(null);
  readonly checkFailed = signal(false);
  readonly formatError = signal<string | null>(null);

  readonly importing = signal(false);
  readonly importResult = signal<AgentTeamImportResponse | null>(null);
  readonly importFailed = signal(false);
  readonly importInvalid = signal<AgentTeamInvalidFehler[]>([]);

  readonly selected = signal<ReadonlySet<string>>(new Set());
  readonly deactivated = signal<ReadonlySet<string>>(new Set());
  readonly deactivationFailed = signal<ReadonlySet<string>>(new Set());
  readonly deactivating = signal(false);
  readonly confirmingDeactivation = signal(false);

  readonly busy = computed(() => this.checking() || this.importing() || this.deactivating());

  readonly teams = computed(() => this.checkResult()?.teams ?? []);

  readonly hasChanges = computed(() =>
    this.teams().some((team) =>
      team.konten.some(
        (konto) => konto.stand === 'NEU' || konto.fehlendeMitgliedschaften.length > 0 || konto.tokenWirdErzeugt,
      ),
    ),
  );

  readonly allPresent = computed(() => !!this.checkResult()?.fehlerfrei && !this.hasChanges());

  readonly canSubmit = computed(
    () => !!this.checkResult()?.fehlerfrei && this.hasChanges() && !this.importResult() && !this.busy(),
  );

  readonly nichtMehrInDatei = computed(() => this.teams().flatMap((team) => team.nichtMehrInDatei));

  readonly kontoColumns = KONTO_COLUMNS;
  readonly nichtMehrColumns = NICHT_MEHR_COLUMNS;

  readonly resultStats = computed((): StatItem[] | null => {
    const result = this.importResult();
    if (!result) return null;
    return [
      {
        label: this.translate.instant('agentTeam.result.konten'),
        value: result.konten.filter((konto) => konto.angelegt).length,
      },
      {
        label: this.translate.instant('agentTeam.result.mitgliedschaften'),
        value: result.konten.reduce((sum, konto) => sum + konto.mitgliedschaftenGeaendert, 0),
      },
      { label: this.translate.instant('agentTeam.result.tokens'), value: result.tokens.length },
    ];
  });

  readonly hasNewTokens = computed(() => (this.importResult()?.tokens.length ?? 0) > 0);

  async onFileSelected(file: File): Promise<void> {
    if (this.busy()) return;
    this.reset();
    if (file.size > MAX_FILE_BYTES) {
      this.fileError.set('tooLarge');
      return;
    }
    const teamFile = await parseJsonObject(file);
    if (teamFile === null) {
      this.fileError.set('invalidJson');
      return;
    }
    this.teamFile = teamFile;
    this.runCheck();
  }

  onFileCleared(): void {
    if (!this.busy()) this.reset();
  }

  runCheck(): void {
    this.checking.set(true);
    this.checkFailed.set(false);
    this.formatError.set(null);
    this.agentTeamService
      .check(this.customerId, this.teamFile)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          this.checking.set(false);
          this.checkResult.set(result);
        },
        error: (err: HttpErrorResponse) => {
          this.checking.set(false);
          const formatMessage = this.formatMessage(err);
          if (formatMessage !== null) this.formatError.set(formatMessage);
          else this.checkFailed.set(true);
        },
      });
  }

  onSubmit(): void {
    if (!this.canSubmit()) return;
    this.importing.set(true);
    this.importFailed.set(false);
    this.importInvalid.set([]);
    this.formatError.set(null);
    this.agentTeamService
      .importTeams(this.customerId, this.teamFile)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          this.importing.set(false);
          this.importResult.set(result);
          this.download();
        },
        error: (err: HttpErrorResponse) => {
          this.importing.set(false);
          const body = this.errorBody(err);
          const formatMessage = this.formatMessage(err);
          if (err.status === 400 && body?.code === 'AGENT_TEAM_INVALID' && body.fehler?.length) {
            this.importInvalid.set(body.fehler);
          } else if (formatMessage !== null) {
            this.formatError.set(formatMessage);
          } else {
            this.importFailed.set(true);
          }
        },
      });
  }

  download(): void {
    const tokens = this.importResult()?.tokens ?? [];
    if (tokens.length === 0) return;
    const content = buildAgentEnvFile(tokens.map((token) => ({ kuerzel: token.kuerzel, token: token.wert })));
    saveBlob(new Blob([content], { type: 'text/plain;charset=utf-8' }), AGENT_ENV_FILE_NAME);
  }

  canDeactivate(account: AgentTeamNichtMehrInDatei): boolean {
    return account.status === 'ACTIVE' && !this.deactivated().has(account.userId);
  }

  isDeactivated(account: AgentTeamNichtMehrInDatei): boolean {
    return this.deactivated().has(account.userId);
  }

  isAlreadyInactive(account: AgentTeamNichtMehrInDatei): boolean {
    return account.status === 'INACTIVE';
  }

  hasFehler(konto: AgentTeamCheckKonto): boolean {
    return konto.stand === 'FEHLER';
  }

  onSelectionChange(userId: string, checked: boolean): void {
    const next = new Set(this.selected());
    if (checked) next.add(userId);
    else next.delete(userId);
    this.selected.set(next);
  }

  requestDeactivation(): void {
    if (this.selected().size > 0 && !this.busy()) this.confirmingDeactivation.set(true);
  }

  cancelDeactivation(): void {
    this.confirmingDeactivation.set(false);
  }

  confirmDeactivation(): void {
    const accounts = this.nichtMehrInDatei().filter((account) => this.selected().has(account.userId));
    this.confirmingDeactivation.set(false);
    if (accounts.length === 0) return;
    this.deactivating.set(true);
    from(accounts)
      .pipe(
        concatMap((account) => this.deactivate(account)),
        toArray(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((outcomes) => {
        const deactivated = new Set(this.deactivated());
        const failed = new Set<string>();
        for (const outcome of outcomes) {
          if (outcome.ok) deactivated.add(outcome.userId);
          else failed.add(outcome.userId);
        }
        this.deactivated.set(deactivated);
        this.deactivationFailed.set(failed);
        this.selected.set(new Set(failed));
        this.deactivating.set(false);
      });
  }

  standKind(stand: AgentTeamStand): StatusKind {
    return STAND_KIND[stand];
  }

  accountStatusKind(account: AgentTeamNichtMehrInDatei): StatusKind {
    return this.isDeactivated(account) ? 'inaktiv' : customerStatusKind(account.status);
  }

  fullName(konto: Pick<AgentTeamCheckKonto, 'vorname' | 'nachname'>): string {
    return `${konto.vorname} ${konto.nachname}`.trim();
  }

  projectLabel(mitgliedschaft: AgentTeamMitgliedschaft): string {
    return mitgliedschaft.projektName ?? mitgliedschaft.projektId;
  }

  /** The file may name any role; only these two have a label, others are shown as written. */
  isKnownRole(rolle: string): boolean {
    return KNOWN_ROLES.has(rolle);
  }

  navigateBack(): void {
    if (this.busy()) return;
    void this.router.navigate(['/client-management', this.customerId]);
  }

  private deactivate(account: AgentTeamNichtMehrInDatei): Observable<DeactivationOutcome> {
    return this.tenantUserService
      .updateUser(this.customerId, account.userId, {
        firstName: account.vorname,
        lastName: account.nachname,
        status: 'INACTIVE',
      })
      .pipe(
        map(() => ({ userId: account.userId, ok: true })),
        catchError(() => of({ userId: account.userId, ok: false })),
      );
  }

  private reset(): void {
    this.teamFile = null;
    this.fileError.set(null);
    this.checkResult.set(null);
    this.checkFailed.set(false);
    this.formatError.set(null);
    this.importResult.set(null);
    this.importFailed.set(false);
    this.importInvalid.set([]);
    this.selected.set(new Set());
    this.deactivated.set(new Set());
    this.deactivationFailed.set(new Set());
  }

  private errorBody(err: HttpErrorResponse): AgentTeamErrorBody | null {
    return typeof err.error === 'object' ? (err.error as AgentTeamErrorBody | null) : null;
  }

  /** The server text names team and value from the file; the template renders it only as text. */
  private formatMessage(err: HttpErrorResponse): string | null {
    const body = this.errorBody(err);
    return err.status === 400 && body?.code === 'AGENT_TEAM_FORMAT' ? (body.error ?? '') : null;
  }
}

/** The server accepts only a JSON object as team file; other JSON roots count as invalid like broken JSON. */
async function parseJsonObject(file: File): Promise<object | null> {
  try {
    const parsed: unknown = JSON.parse(await file.text());
    return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}
